import {
  Box,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Slide,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material"
import React, { useEffect, useState } from "react"
import CloseIcon from "@mui/icons-material/Close"
import { maxWidth600 } from "themes/default/breakpoints"
import { useTranslation } from "react-i18next"
import { useModuleContext } from "contexts/ModuleContext"
import { styles } from "./styles"
import SwapHorizIcon from "@mui/icons-material/SwapHoriz"
import useApi from "modules/translator"
import VolumeUpIcon from "@mui/icons-material/VolumeUp"
import TextToolBar from "components/ActivityContent/TextComprehesion/TextToolBar"

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
})

export default function TranslatorModal({ open, onClose, onSubmit }) {
  const { i18n, t } = useTranslation()
  const { module } = useModuleContext()
  const [fromLanguage, setFromLanguage] = React.useState("en")
  const [toLanguage, setToLanguage] = React.useState("fr")
  const { AVALIALBE_LANGUAGES, translate } = useApi()
  // const [languages, setLanguages] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [fromText, setFromText] = React.useState("")
  const [toText, setToText] = React.useState("")
  const matches = useMediaQuery(maxWidth600)
  const [alternatives, setAlternatives] = React.useState([])
  const [speechSynthesisSupported, setSpeechSynthesisSupported] =
    useState(false)
  const [fontSettings, setFontSettings] = useState({
    fontSize: undefined,
    fontFamily: undefined,
    bgColor: undefined,
  })

  useEffect(() => {
    if ("speechSynthesis" in window) {
      setSpeechSynthesisSupported(true)
    }
  }, [])

  useEffect(() => {
    if (!open) {
      setAlternatives([])
      setFromText("")
      setToText("")
    }
  }, [open])

  useEffect(() => {
    // get current language
    const currentLanguage = i18n.language
    if (currentLanguage) {
      const to = currentLanguage
      const template = module?.templateLanguageId
      if (template) {
        const from = AVALIALBE_LANGUAGES.find(
          (lang) => lang.languageId === template
        )?.code
        setFromLanguage(from)
        setToLanguage(to)
      }
    }
  }, [module])

  useEffect(() => {
    // wait 1 second before translating
    const timer = setTimeout(() => {
      if (fromText.length > 0) {
        translateText(fromText)
      }
    }, 1000)
    return () => clearTimeout(timer)
  }, [fromText])

  useEffect(() => {
    if (fromText.length > 0) {
      translateText(fromText)
    }
  }, [fromLanguage, toLanguage])

  const swapLanguages = () => {
    const temp = fromLanguage
    setFromLanguage(toLanguage)
    setToLanguage(temp)
    setFromText(toText)
  }

  const translateText = async (text) => {
    try {
      setLoading(true)
      console.log(`translate from ${fromLanguage} to ${toLanguage}`)
      const response = await translate({
        q: text,
        source: fromLanguage,
        target: toLanguage,
      })
      console.log("🚀 ~ translate ~ response:", response)
      if (response) {
        setToText(response.translatedText)
        if (response?.alternatives?.length > 0) {
          setAlternatives(response.alternatives)
        } else {
          setAlternatives([])
        }
      }
      setLoading(false)
    } catch (error) {
      console.log("🚀 ~ translate ~ error:", error)
      setToText("Error translating text")
      setLoading(false)
    }
  }

  const handleSpeak = (text, lang) => {
    console.log("🚀 ~ handleSpeak ~ text:", text)
    if (speechSynthesisSupported) {
      const utterance = new SpeechSynthesisUtterance(text)
      utterance.lang = lang // Establece el idioma de la síntesis de voz
      console.log("🚀 ~ handleSpeak ~ toLanguage:", lang)
      window.speechSynthesis.speak(utterance)
    } else {
      alert("Speech Synthesis not supported in this browser.")
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      fullWidth
      PaperProps={{
        sx: [
          styles.modalContainer,
          fontSettings.fontFamily && {
            "& .MuiTypography-root, button, textarea": {
              fontFamily: fontSettings.fontFamily,
            },
          },
          fontSettings?.bgColor && {
            backgroundColor: fontSettings.bgColor,
          },
        ],
      }}
    >
      <Box display={"none"}>
        <TextToolBar
          updateFontStyle={(fontFamily) =>
            setFontSettings((prev) => ({ ...prev, fontFamily }))
          }
          updateFontSize={(fontSize) =>
            setFontSettings((prev) => ({ ...prev, fontSize }))
          }
          updateBgColor={(bgColor) =>
            setFontSettings((prev) => ({ ...prev, bgColor }))
          }
        />
      </Box>
      <DialogContent>
        <IconButton onClick={onClose} sx={styles.closeButton}>
          <CloseIcon />
        </IconButton>
        <Stack
          direction={matches ? "column" : "row"}
          position={"relative"}
          sx={styles.table}
        >
          <Stack
            sx={[
              styles.incell,
              fontSettings?.bgColor && {
                backgroundColor: fontSettings.bgColor,
              },
            ]}
          >
            <Stack sx={styles.cellContent}>
              <FormControl sx={styles.selectForm} size="small">
                <Select
                  value={fromLanguage}
                  label=""
                  sx={[
                    styles.select,
                    fontSettings?.fontFamily && {
                      fontFamily: fontSettings.fontFamily,
                    },
                    fontSettings?.bgColor && {
                      "& .MuiPaper-root": {
                        backgroundColor: fontSettings.bgColor,
                      },
                    },
                  ]}
                  onChange={(e) => setFromLanguage(e.target.value)}
                >
                  {AVALIALBE_LANGUAGES.map((lang) => (
                    <MenuItem
                      key={lang.code}
                      value={lang.code}
                      sx={[
                        fontSettings?.fontFamily && {
                          fontFamily: fontSettings.fontFamily,
                        },
                      ]}
                    >
                      {lang.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                sx={styles.tta_intxt}
                placeholder={t("enter_text")}
                fullWidth
                multiline
                value={fromText}
                onChange={(e) => setFromText(e.target.value)}
              />
            </Stack>
            {fromText?.length > 0 && speechSynthesisSupported && (
              <IconButton
                sx={styles.speakButton}
                onClick={() => handleSpeak(fromText, fromLanguage)}
              >
                <VolumeUpIcon />
              </IconButton>
            )}
          </Stack>
          <Stack sx={styles.tta_swapcell}>
            <IconButton sx={styles.swapButton} onClick={swapLanguages}>
              <SwapHorizIcon />
            </IconButton>
          </Stack>
          <Stack sx={styles.outcell}>
            <Stack sx={styles.cellContent}>
              <FormControl sx={styles.selectForm} size="small">
                <Select
                  value={toLanguage}
                  label=""
                  sx={[
                    styles.select,
                    fontSettings?.fontFamily && {
                      fontFamily: fontSettings.fontFamily,
                    },
                    fontSettings?.bgColor && {
                      "& .MuiList-root": {
                        backgroundColor: fontSettings.bgColor,
                      },
                    },
                  ]}
                  onChange={(e) => setToLanguage(e.target.value)}
                >
                  {AVALIALBE_LANGUAGES.map((lang) => (
                    <MenuItem
                      key={lang.code}
                      value={lang.code}
                      sx={[
                        fontSettings?.fontFamily && {
                          fontFamily: fontSettings.fontFamily,
                        },
                      ]}
                    >
                      {lang.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                sx={styles.tta_intxt}
                placeholder={t("translation")}
                fullWidth
                multiline
                contentEditable={false}
                editable={false}
                disabled
                value={loading ? "..." : toText}
              />
            </Stack>
            {toText?.length > 0 && speechSynthesisSupported && (
              <IconButton
                sx={[styles.speakButton, styles.speakButtonOut]}
                onClick={() => handleSpeak(toText, toLanguage)}
              >
                <VolumeUpIcon />
              </IconButton>
            )}
            {!loading && alternatives?.length > 0 && (
              <Stack sx={styles.moreOutput}>
                <Typography variant="span" sx={styles.moreTitle}>
                  {t("more_translations")}
                </Typography>
                <Stack>
                  {alternatives.map((alt, index) => (
                    <Stack key={index} sx={styles.moreItem}>
                      <Typography variant="span" sx={styles.moreText}>
                        {alt}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            )}
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}
